import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, EmptyState, SectionHeading } from '@/components/ui';
import { formatDate, formatTime, startOfMonth, todayIso } from '@/domain/dates';
import { formatEur, formatKm } from '@/domain/money';
import type { Invoice, Trip } from '@/domain/types';
import { openInvoices, tripsInPeriod } from '@/db/repositories';
import { useSession } from '@/session/SessionContext';
import { passengerDisplayName } from '@/suggest';
import { formatLastBackup } from '@/features/backup/backupStatus';
import { useT } from '@/i18n';

export function DashboardScreen() {
  const session = useSession();
  const t = useT();
  const [monthTrips, setMonthTrips] = useState<Trip[] | null>(null);
  const [outstanding, setOutstanding] = useState<Invoice[]>([]);

  useEffect(() => {
    void (async () => {
      const today = todayIso();
      setMonthTrips(await tripsInPeriod(session.dataKey, startOfMonth(today), today));
      setOutstanding(await openInvoices(session.dataKey));
    })();
  }, [session.dataKey]);

  const distance = monthTrips?.reduce((sum, trip) => sum + trip.distanceMeters, 0) ?? 0;
  const revenue = monthTrips?.reduce((sum, trip) => sum + trip.fareGrossCents, 0) ?? 0;
  const uninvoiced = monthTrips?.filter((trip) => !trip.invoiceId) ?? [];
  const outstandingCents = outstanding.reduce((sum, invoice) => sum + invoice.grossTotalCents, 0);

  return (
    <div className="space-y-5">
      <SectionHeading
        title={t.dashboard.greeting(session.account.displayName.split(' ')[0] ?? '')}
        description={formatDate(todayIso())}
        action={
          <Link to="/fahrten/neu" className="btn-primary">
            {t.dashboard.newTrip}
          </Link>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label={t.dashboard.tripsThisMonth}
          value={String(monthTrips?.length ?? t.common.dash)}
        />
        <Stat
          label={t.dashboard.kilometers}
          value={monthTrips ? `${formatKm(distance)} km` : t.common.dash}
        />
        <Stat
          label={t.dashboard.revenueGross}
          value={monthTrips ? formatEur(revenue) : t.common.dash}
        />
        <Stat
          label={t.dashboard.outstanding}
          value={formatEur(outstandingCents)}
          tone={outstanding.length > 0 ? 'text-amber-700' : undefined}
        />
      </div>

      {uninvoiced.length > 0 && (
        <Card
          title={t.dashboard.uninvoiced(uninvoiced.length)}
          action={
            <Link to="/rechnungen/neu" className="btn-secondary text-xs">
              {t.dashboard.createInvoice}
            </Link>
          }
        >
          <p className="text-muted text-sm">
            {t.dashboard.uninvoicedBody(
              formatEur(uninvoiced.reduce((sum, trip) => sum + trip.fareGrossCents, 0)),
            )}
          </p>
        </Card>
      )}

      <Card
        title={t.dashboard.recent}
        action={
          <Link to="/fahrten" className="text-muted hover:text-heading text-xs">
            {t.dashboard.showAll}
          </Link>
        }
      >
        {!monthTrips ? (
          <p className="text-muted text-sm">{t.common.loading}</p>
        ) : monthTrips.length === 0 ? (
          <EmptyState title={t.dashboard.noTrips}>
            <Link to="/fahrten/neu" className="text-accent underline">
              {t.dashboard.firstTrip}
            </Link>
          </EmptyState>
        ) : (
          <ul className="divide-line -my-2 divide-y">
            {[...monthTrips]
              .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
              .slice(0, 6)
              .map((trip) => {
                const passenger = session.passengers.find((p) => p.id === trip.passengerId);
                return (
                  <li key={trip.id}>
                    <Link
                      to={`/fahrten/${trip.id}`}
                      className="flex items-center justify-between gap-3 py-2.5"
                    >
                      <div className="min-w-0">
                        <p className="text-heading truncate text-sm">
                          {passenger ? passengerDisplayName(passenger) : t.common.unknown}
                        </p>
                        <p className="text-muted text-xs">
                          {formatDate(trip.date)} · {formatTime(trip.startedAt)} ·{' '}
                          {formatKm(trip.distanceMeters)} km
                        </p>
                      </div>
                      <span className="text-body shrink-0 text-sm font-medium">
                        {formatEur(trip.fareGrossCents)}
                      </span>
                    </Link>
                  </li>
                );
              })}
          </ul>
        )}
      </Card>

      <Card title={t.dashboard.systemStatus}>
        <ul className="space-y-2 text-sm">
          <li className="flex items-center justify-between gap-3">
            <span className="text-muted">{t.dashboard.lastBackup}</span>
            <span className="flex items-center gap-2">
              <span className="text-heading">{formatLastBackup(session.app, t)}</span>
              <Link to="/sicherung" className="text-accent text-xs underline">
                {t.dashboard.backupLink}
              </Link>
            </span>
          </li>
          <li className="flex items-center justify-between gap-3">
            <span className="text-muted">{t.dashboard.distanceAuthority}</span>
            <Badge tone={session.app.distanceSystemOfRecord === 'computed' ? 'info' : 'neutral'}>
              {session.app.distanceSystemOfRecord === 'computed'
                ? t.dashboard.distanceComputed
                : t.dashboard.distanceOdometer}
            </Badge>
          </li>
          <li className="flex items-center justify-between gap-3">
            <span className="text-muted">{t.dashboard.vat}</span>
            <span className="text-heading">
              {session.company.kleinunternehmer
                ? t.dashboard.vatKleinunternehmer
                : session.company.licenceType === 'taxi'
                  ? t.dashboard.vatTaxi
                  : t.dashboard.vatMietwagen}
            </span>
          </li>
        </ul>
      </Card>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="card">
      <p className="text-muted text-xs">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${tone ?? 'text-heading'}`}>{value}</p>
    </div>
  );
}
